import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { plain } from '../../lib/labels';

/**
 * Logs / Activity — client side of GET /admin/logs (maja_api
 * src/modules/admin/admin-logs.controller.ts). Everything is filtered, searched
 * and paged on the server; the panel only ever holds one page.
 */

export type LogSeverity = 'DEBUG' | 'INFO' | 'NOTICE' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type LogStatus = 'SUCCESS' | 'FAILURE' | 'PENDING';

export interface LogRow {
  id: string;
  createdAt: string;
  eventType: string;
  eventLabel: string | null;
  eventCategory: string;
  kind: 'audit' | 'system';
  severity: LogSeverity;
  status: LogStatus;
  source: string;
  actorType: 'admin' | 'user' | 'listener' | 'system';
  actorId: string | null;
  targetType: string;
  targetId: string | null;
  userId: string | null;
  listenerId: string | null;
  callId: string | null;
  transactionId: string | null;
  paymentId: string | null;
  requestId: string | null;
  correlationId: string | null;
  message: string | null;
  metadata: Record<string, unknown> | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  errorCode: string | null;
  platform: string | null;
  appVersion: string | null;
  /** Only for admins with logs.view_sensitive; null otherwise. */
  ipAddress: string | null;
  userAgent: string | null;
  deviceId: string | null;
  errorMessage: string | null;
  /** Timeline only: ms since the previous row. */
  sincePreviousMs?: number | null;
}

export interface LogRefs {
  users: Record<string, { handle: string; displayName: string | null }>;
  listeners: Record<string, { displayName: string; userId: string }>;
  admins: Record<string, { name: string; role: string }>;
}

export interface LogsPage {
  data: LogRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  refs: LogRefs;
}

export interface LogsSummary {
  total: number;
  errors: number;
  critical: number;
  warnings: number;
  successful: number;
  failed: number;
  pending: number;
  byCategory: Record<string, number>;
}

export interface LogsMeta {
  categories: string[];
  severities: LogSeverity[];
  statuses: LogStatus[];
  sources: string[];
  kinds: string[];
  events: Array<{ type: string; label: string; category: string; kind: 'audit' | 'system' }>;
  adminAliases: string[];
  canViewSensitive: boolean;
  limits: { defaultLimit: number; maxLimit: number; exportMax: number };
}

export interface TimelineResponse {
  data: LogRow[];
  callRequestId: string | null;
  truncated: boolean;
  refs: LogRefs;
}

/** The filter params the API takes (snake_case, as the API documents them). */
export interface LogsFilters {
  search?: string;
  from?: string;
  to?: string;
  event_type?: string;
  event_category?: string;
  severity?: string;
  status?: string;
  source?: string;
  kind?: string;
  user_id?: string;
  listener_id?: string;
  admin_id?: string;
  call_id?: string;
  payment_id?: string;
  transaction_id?: string;
  request_id?: string;
  correlation_id?: string;
}

export const FILTER_KEYS: Array<keyof LogsFilters> = [
  'search',
  'from',
  'to',
  'event_type',
  'event_category',
  'severity',
  'status',
  'source',
  'kind',
  'user_id',
  'listener_id',
  'admin_id',
  'call_id',
  'payment_id',
  'transaction_id',
  'request_id',
  'correlation_id',
];

/** Drop empty values so the query key / URL stays canonical. */
export function cleanFilters(f: LogsFilters): LogsFilters {
  const out: LogsFilters = {};
  for (const k of FILTER_KEYS) {
    const v = f[k]?.trim();
    if (v) out[k] = v;
  }
  return out;
}

export function useLogsMeta() {
  return useQuery({
    queryKey: ['admin', 'logs', 'meta'],
    queryFn: async () => (await api.get<LogsMeta>('/admin/logs/meta')).data,
    staleTime: 10 * 60_000,
  });
}

export function useLogsPage(filters: LogsFilters, page: number, limit: number, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'logs', 'list', filters, page, limit],
    queryFn: async () => (await api.get<LogsPage>('/admin/logs', { params: { ...filters, page, limit } })).data,
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useLogsSummary(filters: LogsFilters) {
  return useQuery({
    queryKey: ['admin', 'logs', 'summary', filters],
    queryFn: async () => (await api.get<LogsSummary>('/admin/logs/summary', { params: filters })).data,
    placeholderData: (prev) => prev,
  });
}

export function useTimeline(target: { call_id?: string; user_id?: string; listener_id?: string }, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'logs', 'timeline', target],
    queryFn: async () => (await api.get<TimelineResponse>('/admin/logs/timeline', { params: target })).data,
    enabled,
  });
}

// ---------------------------------------------------------------- display

export const SEVERITY_COLOR: Record<LogSeverity, string> = {
  DEBUG: 'default',
  INFO: 'blue',
  NOTICE: 'cyan',
  WARNING: 'orange',
  ERROR: 'red',
  CRITICAL: 'magenta',
};

export const STATUS_COLOR: Record<LogStatus, string> = {
  SUCCESS: 'green',
  FAILURE: 'red',
  PENDING: 'gold',
};

export const CATEGORY_LABEL: Record<string, string> = {
  AUTH: 'Authentication',
  USER: 'Users',
  LISTENER: 'Listeners',
  CALL: 'Calls',
  WALLET: 'Wallet',
  PAYMENT: 'Payments',
  REFERRAL: 'Referral',
  NOTIFICATION: 'Notifications',
  ADMIN: 'Admin',
  SECURITY: 'Security',
  SYSTEM: 'System',
};

export const SOURCE_LABEL: Record<string, string> = {
  mobile: 'Mobile app',
  backend: 'Backend',
  admin: 'Admin panel',
  payment_gateway: 'Payment gateway',
  call_service: 'Call service',
  notification: 'Notifications',
  background_job: 'Background job',
  system: 'System',
};

/** "CALL_CONNECTED" → "Call connected"; console actions via plain(). */
export function eventLabel(row: Pick<LogRow, 'eventType' | 'eventLabel'>): string {
  if (row.eventLabel) return row.eventLabel;
  if (/^[A-Z][A-Z_]+$/.test(row.eventType)) {
    const s = row.eventType.toLowerCase().replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  return plain(row.eventType);
}

/** "2 s", "1 min 5 s", "3 h 2 min" — the gap between two timeline rows. */
export function formatGap(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

export function formatLogTime(iso: string, withSeconds = true): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  });
}

/** Display name for an actor / user / listener id from the page's refs. */
export function refName(refs: LogRefs | undefined, kind: 'user' | 'listener' | 'admin', id: string | null): string | null {
  if (!id || !refs) return null;
  if (kind === 'admin') return refs.admins[id]?.name ?? null;
  if (kind === 'listener') return refs.listeners[id]?.displayName ?? null;
  const u = refs.users[id];
  return u ? (u.displayName ?? `@${u.handle}`) : null;
}
